import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-components";
import type { ProColumns } from "@ant-design/pro-components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Card, Col, Drawer, Empty, Form, Input, InputNumber,
  message, Popconfirm, Row, Space, Spin, Switch, Tag, Typography,
} from "antd";
import { useState } from "react";
import {
  addEntry, addItem, addLevel, createTemplate, deleteEntry,
  deleteItem, deleteLevel, deleteTemplate, getTemplate, listTemplates,
  type TemplateDetail, type TemplateListItem,
} from "./api";

// ===== 列表 =====

const COLUMNS: ProColumns<TemplateListItem>[] = [
  { title: "名称", dataIndex: "name", ellipsis: true },
  { title: "Code", dataIndex: "code", width: 120 },
  { title: "总分", dataIndex: "score", width: 80, align: "center", search: false },
  { title: "评价项", dataIndex: "items_count", width: 80, align: "center", search: false },
  { title: "等级数", dataIndex: "levels_count", width: 80, align: "center", search: false },
  {
    title: "启用",
    dataIndex: "is_active",
    width: 80,
    search: false,
    render: (_, row) => row.is_active ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
  },
];

// ===== 详情 Drawer =====

function TemplateDetailDrawer({
  tid,
  open,
  onClose,
  onRefreshList,
}: {
  tid: string | null;
  open: boolean;
  onClose: () => void;
  onRefreshList: () => void;
}) {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();

  const detailQ = useQuery({
    queryKey: ["template-detail", tid],
    queryFn: () => (tid ? getTemplate(tid) : Promise.resolve(null as TemplateDetail | null)),
    enabled: tid !== null,
  });

  const reload = () => qc.invalidateQueries({ queryKey: ["template-detail", tid] });

  const delItemM = useMutation({
    mutationFn: deleteItem, onSuccess: () => { reload(); msgApi.success("已删除评价项"); },
  });
  const delEntryM = useMutation({
    mutationFn: deleteEntry, onSuccess: () => { reload(); msgApi.success("已删除条目"); },
  });
  const delLevelM = useMutation({
    mutationFn: deleteLevel, onSuccess: () => { reload(); msgApi.success("已删除等级"); },
  });
  const delM = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      onRefreshList();
      onClose();
      msgApi.success("已删除模板");
    },
  });

  const [itemForm] = Form.useForm();
  const [entryFormByIid, setEntryFormByIid] = useState<Record<string, { name: string; score: number }>>({});
  const [levelForm] = Form.useForm();

  // 注意:以下 useMutation 必须在 `if (!open) return null` 之前,
  // 否则 open 从 false→true 时 hook 数量变化,触发 React minified error #310(hooks 顺序违规)
  const addItemM = useMutation({
    mutationFn: (body: { name: string; score: number }) =>
      addItem(tid!, { name: body.name, score: body.score, order_num: (detailQ.data?.items.length ?? 0) + 1 }),
    onSuccess: () => { reload(); itemForm.resetFields(); msgApi.success("已加评价项"); },
  });

  const addEntryM = useMutation({
    mutationFn: ({ iid, body }: { iid: string; body: { name: string; score: number } }) =>
      addEntry(iid, body),
    onSuccess: (_, v) => { reload(); setEntryFormByIid((p) => ({ ...p, [v.iid]: { name: "", score: 0 } })); msgApi.success("已加条目"); },
  });

  const addLevelM = useMutation({
    mutationFn: (body: { name: string; score: number }) => addLevel(tid!, body),
    onSuccess: () => { reload(); levelForm.resetFields(); msgApi.success("已加等级"); },
  });

  if (!open) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={780}
      title={detailQ.data ? `模板详情:${detailQ.data.name}` : "模板详情"}
      extra={
        detailQ.data && (
          <Popconfirm title="确认删除该模板?" onConfirm={() => delM.mutate(tid!)}>
            <Button danger icon={<DeleteOutlined />} loading={delM.isPending}>
              删除模板
            </Button>
          </Popconfirm>
        )
      }
    >
      {msgCtx}
      {detailQ.isLoading && <Spin />}
      {detailQ.data && (
        <>
          <Card size="small" title="基本信息" style={{ marginBottom: 12 }}>
            <Row gutter={8}>
              <Col span={8}>Code: <b>{detailQ.data.code || "—"}</b></Col>
              <Col span={8}>总分: <b>{detailQ.data.score}</b></Col>
              <Col span={8}>
                启用: {detailQ.data.is_active
                  ? <Tag color="green">是</Tag>
                  : <Tag>否</Tag>}
              </Col>
            </Row>
            {detailQ.data.description && (
              <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                {detailQ.data.description}
              </Typography.Paragraph>
            )}
          </Card>

          <Card size="small" title={`评价项 / 条目(${detailQ.data.items.length})`} style={{ marginBottom: 12 }}>
            {detailQ.data.items.length === 0 && (
              <Empty description="还没有评价项" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
            {detailQ.data.items.map((it) => (
              <Card
                key={it.id}
                type="inner"
                size="small"
                title={
                  <Space>
                    <span>{it.name}</span>
                    <Tag>分值 {it.score}</Tag>
                  </Space>
                }
                extra={
                  <Popconfirm title="删除评价项?" onConfirm={() => delItemM.mutate(it.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                }
                style={{ marginBottom: 10 }}
              >
                {it.entries.length === 0 && (
                  <Typography.Text type="secondary">未添加具体条目</Typography.Text>
                )}
                {it.entries.map((e) => (
                  <div
                    key={e.id}
                    style={{
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between", padding: "4px 0",
                      borderBottom: "1px dashed #f0f0f0",
                    }}
                  >
                    <Space>
                      <span>{e.name}</span>
                      <Tag>分 {e.score}</Tag>
                    </Space>
                    <Popconfirm title="删除?" onConfirm={() => delEntryM.mutate(e.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ))}
                <Space style={{ marginTop: 8 }}>
                  <Input
                    placeholder="新条目名"
                    size="small"
                    value={entryFormByIid[it.id]?.name ?? ""}
                    onChange={(ev) =>
                      setEntryFormByIid((p) => ({
                        ...p,
                        [it.id]: { name: ev.target.value, score: p[it.id]?.score ?? 0 },
                      }))
                    }
                  />
                  <InputNumber
                    placeholder="分值"
                    size="small"
                    min={0}
                    value={entryFormByIid[it.id]?.score ?? 0}
                    onChange={(v) =>
                      setEntryFormByIid((p) => ({
                        ...p,
                        [it.id]: { name: p[it.id]?.name ?? "", score: v ?? 0 },
                      }))
                    }
                  />
                  <Button
                    size="small"
                    type="primary"
                    loading={addEntryM.isPending}
                    onClick={() =>
                      addEntryM.mutate({
                        iid: it.id,
                        body: {
                          name: entryFormByIid[it.id]?.name ?? "",
                          score: entryFormByIid[it.id]?.score ?? 0,
                        },
                      })
                    }
                  >加条目</Button>
                </Space>
              </Card>
            ))}

            <Card type="inner" size="small" title="新建评价项">
              <Form form={itemForm} layout="inline" onFinish={(v) => addItemM.mutate(v)}>
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input placeholder="名称" />
                </Form.Item>
                <Form.Item name="score" initialValue={10}>
                  <InputNumber placeholder="分值" min={0} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={addItemM.isPending}>
                  添加
                </Button>
              </Form>
            </Card>
          </Card>

          <Card size="small" title={`等级阈值(${detailQ.data.levels.length})`}>
            {detailQ.data.levels.length === 0 && (
              <Empty description="尚未定义等级" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
            {detailQ.data.levels.map((l) => (
              <div
                key={l.id}
                style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between", padding: "4px 0",
                  borderBottom: "1px dashed #f0f0f0",
                }}
              >
                <Space>
                  <Tag color="gold">{l.name}</Tag>
                  <span>≥ {l.score} 分</span>
                </Space>
                <Popconfirm title="删除?" onConfirm={() => delLevelM.mutate(l.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ))}
            <Form
              form={levelForm}
              layout="inline"
              style={{ marginTop: 12 }}
              onFinish={(v) => addLevelM.mutate(v)}
            >
              <Form.Item name="name" rules={[{ required: true }]}>
                <Input placeholder="等级名 (A/B/C)" />
              </Form.Item>
              <Form.Item name="score" rules={[{ required: true }]}>
                <InputNumber placeholder="达标分" min={0} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addLevelM.isPending}>
                加等级
              </Button>
            </Form>
          </Card>
        </>
      )}
    </Drawer>
  );
}

// ===== 主页面 =====

export function TemplatesPage() {
  const [openTid, setOpenTid] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, msgCtx] = message.useMessage();
  const qc = useQueryClient();
  const [reloadKey, setReloadKey] = useState(0);

  const createM = useMutation({
    mutationFn: (body: {
      name: string; code?: string; score: number; description?: string; is_active?: boolean;
    }) => createTemplate(body),
    onSuccess: (d) => {
      msgApi.success("已创建,打开配置");
      setCreateOpen(false);
      form.resetFields();
      setReloadKey((k) => k + 1);
      setOpenTid(d.id);
    },
    onError: (e: Error) => msgApi.error(e.message),
  });

  return (
    <div>
      {msgCtx}
      <ProTable<TemplateListItem>
        key={reloadKey}
        headerTitle="评价模板"
        rowKey="id"
        columns={COLUMNS}
        search={false}
        pagination={{ defaultPageSize: 10 }}
        toolBarRender={() => [
          <Button
            key="new"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            新建模板
          </Button>,
        ]}
        request={async ({ current = 1, pageSize = 10 }) => {
          const out = await listTemplates({ page: current, page_size: pageSize });
          return { data: out.items, total: out.total, success: true };
        }}
        onRow={(row) => ({
          style: { cursor: "pointer" },
          onClick: () => setOpenTid(row.id),
        })}
      />

      <Drawer
        open={createOpen}
        title="新建模板"
        onClose={() => setCreateOpen(false)}
        width={480}
      >
        <Form form={form} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code(代码引用)">
            <Input placeholder="例:trust-v1" />
          </Form.Item>
          <Form.Item name="score" label="总分" initialValue={100}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={createM.isPending}>
            创建
          </Button>
        </Form>
      </Drawer>

      <TemplateDetailDrawer
        tid={openTid}
        open={openTid !== null}
        onClose={() => setOpenTid(null)}
        onRefreshList={() => {
          qc.invalidateQueries({ queryKey: ["templates"] });
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}
